import React from "react";
import { X, Loader2, Inbox, ChevronDown } from "lucide-react";

export function Button({ children, variant = "primary", size = "md", className = "", ...props }) {
  const variants = {
    primary: "bg-navy text-white hover:bg-navy-light",
    secondary: "bg-teal text-white hover:opacity-90",
    outline: "border border-slate-300 bg-white text-slate-700 hover:bg-slate-50",
    danger: "bg-red-600 text-white hover:bg-red-700",
    ghost: "text-slate-600 hover:bg-slate-100",
    success: "bg-emerald-600 text-white hover:bg-emerald-700",
    warning: "bg-amber-500 text-white hover:bg-amber-600",
  };
  const sizes = { sm: "px-3 py-1.5 text-xs", md: "px-4 py-2 text-sm", lg: "px-6 py-3 text-base" };
  return (
    <button
      className={`inline-flex items-center justify-center gap-2 rounded-md font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-teal focus:ring-offset-2 ${variants[variant]} ${sizes[size]} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
}

export function Input({ className = "", ...props }) {
  return (
    <input
      className={`w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder-slate-400 transition-colors focus:border-teal focus:outline-none focus:ring-1 focus:ring-teal ${className}`}
      {...props}
    />
  );
}

export function Textarea({ className = "", ...props }) {
  return (
    <textarea
      className={`w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder-slate-400 transition-colors focus:border-teal focus:outline-none focus:ring-1 focus:ring-teal ${className}`}
      {...props}
    />
  );
}

export function Select({ className = "", children, ...props }) {
  return (
    <div className="relative">
      <select
        className={`w-full appearance-none rounded-md border border-slate-300 bg-white px-3 py-2 pr-8 text-sm text-slate-900 transition-colors focus:border-teal focus:outline-none focus:ring-1 focus:ring-teal ${className}`}
        {...props}
      >
        {children}
      </select>
      <ChevronDown size={14} className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
    </div>
  );
}

export function Field({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {children}
    </div>
  );
}

export function Card({ children, className = "", ...props }) {
  return (
    <div className={`rounded-lg border border-slate-200 bg-white ${className}`} {...props}>
      {children}
    </div>
  );
}

export const STATUS_STYLES = {
  pending: "bg-slate-100 text-slate-700 border-slate-300",
  fit: "bg-emerald-50 text-emerald-700 border-emerald-300",
  temp_unfit: "bg-amber-50 text-amber-700 border-amber-300",
  unfit: "bg-red-50 text-red-700 border-red-300",
  permanently_unfit: "bg-rose-100 text-rose-800 border-rose-400",
};

export const STATUS_LABELS = {
  pending: "Pending",
  fit: "Accepted",
  temp_unfit: "Further Evaluation Required",
  unfit: "Not Recommended",
  permanently_unfit: "Permanently Unfit",
};

export function StatusBadge({ status, className = "" }) {
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${STATUS_STYLES[status] || STATUS_STYLES.pending} ${className}`}
      data-testid={`status-badge-${status}`}
    >
      {STATUS_LABELS[status] || status}
    </span>
  );
}

export function Badge({ children, color = "slate", className = "", ...rest }) {
  const colors = {
    slate: "bg-slate-100 text-slate-700",
    navy: "bg-navy/10 text-navy",
    teal: "bg-teal-light text-teal",
    green: "bg-emerald-50 text-emerald-700",
    red: "bg-red-50 text-red-700",
    amber: "bg-amber-50 text-amber-700",
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${colors[color]} ${className}`} {...rest}>
      {children}
    </span>
  );
}

export function Modal({ open, onClose, title, children, wide }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/50" onClick={onClose} />
      <div className={`relative max-h-[90vh] w-full ${wide ? "max-w-2xl" : "max-w-md"} overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-xl fade-up`}>
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <h3 className="font-heading text-lg font-bold text-slate-900">{title}</h3>
          <button onClick={onClose} className="rounded p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600" data-testid="modal-close-btn">
            <X size={18} />
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

export function Spinner({ className = "" }) {
  return <Loader2 className={`animate-spin text-navy ${className}`} size={28} />;
}

export function EmptyState({ text = "No records found" }) {
  return (
    <div className="flex flex-col items-center gap-2 py-12 text-slate-400">
      <Inbox size={32} />
      <p className="text-sm">{text}</p>
    </div>
  );
}

export function PageHeader({ title, subtitle, children }) {
  return (
    <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
      <div>
        <h1 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
      </div>
      {children && <div className="flex flex-wrap items-center gap-3">{children}</div>}
    </div>
  );
}

export function Table({ headers, children }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-left text-sm">
        <thead>
          <tr className="border-b border-slate-200">
            {headers.map((h) => (
              <th key={h} className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}
